import { useLazyQuery } from '@apollo/client'

import { APIStatus } from '@flowwai-frontend/shared/api-common'
import {
   convertToErrorType,
   CustomError,
   getAPIStatusFromNetworkStatus
} from '@flowwai-frontend/bps-officer-web/common/data-access'
import { GetThreadsForUserParams } from '@flowwai-frontend/bps-officer-web/graphql'
import { ThreadModel } from '@flowwai-frontend/shared-web/ai-chat-support/data-access'

import {
   GetThreadsForUserQuery as Query,
   GetThreadsForUserDocument as QueryDocument,
   GetThreadsForUserQueryVariables as QueryVariables
} from './query.generatedTypes'
import useAPIResponseHandler from './responseHandler'

export interface FailureArgsType {
   error?: Error | null
   data?: CustomError<Query['getThreadsForUser']>
}

interface TriggerAPIArgsType {
   reqObj: GetThreadsForUserParams

   onFailure?: (args: FailureArgsType) => void
   onSuccess?: () => void
}

export interface ResponseType {
   threads: ThreadModel[]
   totalCount: number
}

interface ReturnType {
   apiError: Error | null
   apiStatus: APIStatus

   triggerAPI: (args: TriggerAPIArgsType) => Promise<ResponseType | null>
   fetchMoreAPI: (args: TriggerAPIArgsType) => Promise<ResponseType | null>
}

const useGetThreadsForUser = (): ReturnType => {
   const [
      getThreadsForUser,
      { error, networkStatus, data, fetchMore }
   ] = useLazyQuery<Query, QueryVariables>(QueryDocument)

   const { handleAPIResponse } = useAPIResponseHandler()

   const triggerAPI = async (
      args: TriggerAPIArgsType
   ): Promise<ResponseType | null> => {
      const requestObj: QueryVariables = {
         params: args.reqObj
      }

      const response = await getThreadsForUser({
         variables: requestObj,
         fetchPolicy: 'network-only',

         onError(error) {
            args.onFailure?.({ error })
         }
      })
      if (!response.data) return null

      return handleAPIResponse({
         data: response.data,

         onHandleError: (
            errorMessage: string,
            data: Query['getThreadsForUser']
         ) => args.onFailure?.({ data: new CustomError(errorMessage, data) }),
         onSuccess: args.onSuccess
      })
   }

   const fetchMoreAPI = async (
      args: TriggerAPIArgsType
   ): Promise<ResponseType | null> => {
      const requestObj: QueryVariables = {
         params: args.reqObj
      }

      const response = await fetchMore({
         variables: requestObj
      })
      if (!response.data) return null

      return handleAPIResponse({
         data: response.data,

         onHandleError: (
            errorMessage: string,
            data: Query['getThreadsForUser']
         ) => args.onFailure?.({ data: new CustomError(errorMessage, data) }),
         onSuccess: args.onSuccess
      })
   }

   const apiError = convertToErrorType(error)
   const apiStatus = getAPIStatusFromNetworkStatus(networkStatus, data)

   return {
      apiError,
      apiStatus,

      triggerAPI,
      fetchMoreAPI
   }
}

export default useGetThreadsForUser
